import { useState } from 'react';
import { CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Post } from './shared/postsData';

interface ROICalculatorProps {
  platform: string;
  posts: Post[];
}
const safeNumber = (value: any) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};
export function ROICalculator({ platform, posts }: ROICalculatorProps) {
  const [customSpend, setCustomSpend] = useState('');
  const [useCustomSpend, setUseCustomSpend] = useState(false);

  const PLATFORM_MAP: any = {
  instagram: ["reel", "video", "carousel", "image"],
  facebook: ["post", "photo", "video", "image"],
  youtube: ["short", "video"],
};

const normalize = (v: any) => String(v || "").toLowerCase();
const filterPosts = () => {
  if (platform === "all") return posts;
  return posts.filter(p => p.type); // include all posts with type
};
  // Calculate total spent from posts
const calculatePostsSpend = () => {
  const filteredPosts = filterPosts();
  return filteredPosts.reduce((total, post) => {
    return total + safeNumber(post.cost);
  }, 0);
};

  // Calculate campaign metrics from posts
const calculateCampaignMetrics = () => {
  const filteredPosts = filterPosts();

  const totalViews = filteredPosts.reduce(
    (sum, post) => sum + safeNumber(post.views),
    0
  );

  const totalLikes = filteredPosts.reduce(
    (sum, post) => sum + safeNumber(post.likes),
    0
  );

  const totalEngagement = filteredPosts.reduce(
    (sum, post) =>
      sum +
      safeNumber(post.likes) +
      safeNumber(post.comments) +
      safeNumber(post.shares),
    0
  );

  return { totalViews, totalLikes, totalEngagement };
};


  const postsSpend = calculatePostsSpend();
  const actualSpend = useCustomSpend ? safeNumber(customSpend) : postsSpend;
  const { totalViews, totalLikes, totalEngagement } = calculateCampaignMetrics();

  // Calculate cost metrics
  const costPerView = totalViews > 0 ? actualSpend / totalViews : 0;
  const costPerLike = totalLikes > 0 ? actualSpend / totalLikes : 0;
  const costPerEngagement = totalEngagement > 0 ? actualSpend / totalEngagement : 0;

  const formatMetric = (value: number) => {
  return value > 0 ? `₹${value.toFixed(2)}` : "--";
};
  const formatCount = (value: number, label: string) => {
    return value > 0 ? `${value.toLocaleString()} ${label}` : ``;
  };
  return (
    <div className="space-y-4">
      {/* Campaign Spend Section */}
      <div className="space-y-2">
        <Label className="text-slate-700">Campaign Spend</Label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            {useCustomSpend ? (
              <Input
                type="number"
                placeholder="Enter custom spend"
                value={customSpend}
                onChange={(e) => setCustomSpend(e.target.value)}
                step="0.01"
                min="0"
              />
            ) : (
              <div className="px-3 py-2 bg-white border border-slate-300 rounded-md">
                ₹{postsSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setUseCustomSpend(!useCustomSpend);
              setCustomSpend('');
            }}
          >
            {useCustomSpend ? 'Use Posts' : 'Custom'}
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          {useCustomSpend 
            ? 'Enter a custom spend amount' 
            : `Calculated from ${platform === 'all' ? 'all' : platform} posts with costs`}
        </p>
      </div>

      {/* Cost Metrics */}
        <div className="pt-4 border-t space-y-3">
          <p className="text-sm text-slate-700">Cost Metrics</p>
          <div className="grid grid-cols-3 gap-3">

            <div className="p-3 rounded-lg border">
              <p className="text-xs text-slate-600 mb-1">Cost Per View</p>
              <p className="text-slate-900">{formatMetric(costPerView)}</p>
              <p className="text-xs text-slate-500 mt-1">{formatCount(totalViews, "views")}</p>
            </div>

            <div className="p-3 rounded-lg border">
              <p className="text-xs text-slate-600 mb-1">Cost Per Like</p>
              <p className="text-slate-900">{formatMetric(costPerLike)}</p>
              <p className="text-xs text-slate-500 mt-1">{formatCount(totalLikes, "likes")}</p>
            </div>

            <div className="p-3 rounded-lg border">
              <p className="text-xs text-slate-600 mb-1">Cost Per Engagement</p>
              <p className="text-slate-900">{formatMetric(costPerEngagement)}</p>
              <p className="text-xs text-slate-500 mt-1">{formatCount(totalEngagement, "engagements")}</p>
            </div>

          </div>
        </div>
      
    </div>
  );
}
